import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import _ from "lodash";
import cx from "classnames";

import styles from "./create.module.scss";
import {
  Accordion,
  Button,
  Form,
  Grid,
  Icon,
  Label,
  Modal,
} from "semantic-ui-react";

import {
  createIADBMetadata,
  fetchIADBMetadatas,
  updateIADBMetadata,
} from "../../../../actions/iadb";

import SaveChanges from "components/ui/Buttons/SaveButton";
import Cancel from "components/ui/Buttons/CancelButton";
import { toast } from "react-toastify";

const initMetadata = {
  id: null,
  page: null,
  itemId: null,
  documentNumber: null,
  language: null,
  assetMappings: [],
};

const initErrorFields = [
  {
    name: "page",
    title: "Page",
    error: false,
    message: null,
  },
  {
    name: "itemId",
    title: "Item Id",
    error: false,
    message: null,
  },
  {
    name: "documentNumber",
    title: "Document Number",
    error: false,
    message: null,
  },
  {
    name: "language",
    title: "Language",
    error: false,
    message: null,
  },
];

export default function CreateMetaData({account, metadata, open, setOpen }) {
  const dispatch = useDispatch();

  const [initialRendering, setInitialRendering] = useState(true);

  const [iadbMetadata, setIADBMetadata] = useState(initMetadata);
  const [error, setError] = useState(null);
  const [errorFields, setErrorFields] = useState(initErrorFields);
  const [isFormSubmitted, setFormSubmitted] = useState(false);
  const [isAssetAdded, setAssetAdded] = useState(false);
  const [assetMappings, setAssetMappings] = useState([]);

  const [mapping, setMapping] = useState({
    asset: "",
    section: "",
  });

  const creatingMetadata = useSelector((state) =>
    _.get(state, "iadb.createIADBMetadata")
  );
  const updatingMetadata = useSelector((state) =>
    _.get(state, "iadb.updateIADBMetadata")
  );

  useEffect(() => {
    setIADBMetadata(initMetadata);
    setErrorFields([...initErrorFields]);
    setError(null);
    setFormSubmitted(false);
    setAssetAdded(false);
    setAssetMappings([]);
    if (metadata !== null && metadata !== undefined) {
      setIADBMetadata({
        id: metadata.id,
        page: metadata.page,
        itemId: metadata.itemId,
        documentNumber: metadata.documentNumber,
        language: metadata.language,
        assetMappings: metadata.assetMappings,
      });

      setAssetMappings(metadata.assetMappings);
    }
  }, [open]);

  useEffect(() => {
    validateFields();

    if (isFormSubmitted) {
      // reset form if iadbMetadata trying to resets the value
      setFormSubmitted(false);
    }
  }, [iadbMetadata]);

  useEffect(() => {
    if (!initialRendering && creatingMetadata && !creatingMetadata.creating) {
      if (creatingMetadata.success) {
        setOpen(false);
        iadbMetadata.page !== null &&
          toast.success(`created successfully`, {
            toastId: "created",
          });
      } else if (creatingMetadata.error) {
        setError(creatingMetadata.error);
      }
    }
  }, [creatingMetadata]);

  useEffect(() => {
    if (!initialRendering && updatingMetadata && !updatingMetadata.updating) {
      if (updatingMetadata.success) {
        setOpen(false);
        iadbMetadata.page !== null &&
          toast.success(`updated successfully`, {
            toastId: "updated",
          });
      } else if (updatingMetadata.error) {
        setError(updatingMetadata.error);
      }
    }
  }, [updatingMetadata]);

  useEffect(() => {
    let message = null;
    for (var i = 0; i < errorFields.length; i++) {
      if (errorFields[i].error) {
        message = errorFields[i].message;
        break;
      }
    }
    setError(message);
  }, [errorFields]);

  useEffect(() => {
    setIADBMetadata({ ...iadbMetadata, assetMappings: assetMappings });
  }, [assetMappings]);

  setTimeout(() => {
    setInitialRendering(false);
  }, 3000);

  const isFieldError = (fieldName) => {
    let error = false;
    if (isFormSubmitted) {
      errorFields.forEach((field, idx) => {
        if (field.name === fieldName && field.error && !Array.isArray(field)) {
          error = true;
        }
        if (Array.isArray(field)) {
          field.forEach((item) => {
            if (item.name === fieldName) {
              error = false;
            }
          });
        }
      });
    }

    return error;
  };

  const isFormValid = () => {
    let errors = errorFields.filter((item) => item.error);
    if (errors.length > 0) return false;

    return true;
  };

  const isAssetMappingsValid = () => {
    if (assetMappings.length === 0) {
      setError("Asset Mappings is required");
      return false;
    }
    return true;
  };

  const validateFields = () => {
    let fields = [];
    let field = "";
    let value = "";

    for (var i = 0; i < errorFields.length; i++) {
      field = errorFields[i].name;
      value = iadbMetadata[field];

      errorFields[i].error =
        value === null || value === undefined ? true : false;

      if (!value) {
        errorFields[i].error = true;
        errorFields[i].message = errorFields[i].title + " is required";
      } else {
        errorFields[i].error = false;
        errorFields[i].message = null;
      }

      if (assetMappings.length === 0) {
        setError("Asset Mappings is required");
      }

      fields.push(errorFields[i]);
    }

    setErrorFields(fields);
  };

  const onCreateMetaData = (e) => {
    e.preventDefault();
    console.log(e);
    setFormSubmitted(true);

    if (isFormValid() && isAssetMappingsValid()) {
      if (metadata) {
        dispatch(
          updateIADBMetadata({ accountId: account.id.toString(), id: iadbMetadata.id, metadata: iadbMetadata })
        );
      }
      if (!metadata) {
        dispatch(createIADBMetadata({ accountId: account.id.toString(), metadata: iadbMetadata }));
      }
    }
  };

  const handleAddClick = () => {
    if (mapping.asset.length === 0 && mapping.section.length === 0) {
      setAssetAdded(true);
      setError("Cannot add empty asset mapping");
    } else if (mapping.asset.length === 0) {
      setAssetAdded(true);
      setError("Asset is required");
    } else if (mapping.section.length === 0) {
      setAssetAdded(true);
      setError("Section is required");
    } else {
      setAssetAdded(false);
      setAssetMappings([
        ...assetMappings,
        { asset: mapping.asset, section: mapping.section },
      ]);
      setMapping({
        asset: "",
        section: "",
      });
    }
  };

  const handleCancelClick = () => {
    setIADBMetadata(initMetadata);
    setAssetAdded(false);
    setFormSubmitted(false);
    setOpen(false);
  };

  const handleRemoveMapping = (index) => {
    var items = [...assetMappings];
    items.splice(index, 1);
    setAssetMappings(items);
  };

  const renderContent = () => {
    if (!metadata) {
      return creatingMetadata && creatingMetadata.creating
        ? "Creating"
        : "Create";
    }

    return updatingMetadata && updatingMetadata.updating
      ? "Updating"
      : "Update";
  };

  return (
    <>
      <Modal
        size="tiny"
        open={open}
        centered={false}
        onClose={() => handleCancelClick()}
        onOpen={() => setOpen(true)}
        className={cx(styles.create)}
      >
        <Modal.Content className={cx(styles.content)}>
          <Form>
            <Grid>
              <Grid.Row>
                <Grid.Column textAlign="center">
                  <span className={cx(styles.title)}>
                    {metadata ? "UPDATE METADATA" : "CREATE METADATA"}
                  </span>
                </Grid.Column>
              </Grid.Row>

              <Grid.Row>
                <Grid.Column>
                  <Form.Input
                    fluid
                    id="page"
                    label={"Page"}
                    placeholder="Page"
                    value={iadbMetadata.page}
                    onChange={(e) =>
                      setIADBMetadata({ ...iadbMetadata, page: e.target.value })
                    }
                    error={isFieldError("page")}
                  ></Form.Input>
                </Grid.Column>
              </Grid.Row>

              <Grid.Row>
                <Grid.Column>
                  <Form.Input
                    fluid
                    id="itemId"
                    label={"Item Id"}
                    placeholder="Item Id"
                    value={iadbMetadata.itemId}
                    onChange={(e) =>
                      setIADBMetadata({
                        ...iadbMetadata,
                        itemId: e.target.value,
                      })
                    }
                    error={isFieldError("itemId")}
                  ></Form.Input>
                </Grid.Column>
              </Grid.Row>

              <Grid.Row>
                <Grid.Column>
                  <Form.Input
                    fluid
                    id="documentNumber"
                    label={"Document Number"}
                    placeholder="Document Number"
                    value={iadbMetadata.documentNumber}
                    onChange={(e) =>
                      setIADBMetadata({
                        ...iadbMetadata,
                        documentNumber: e.target.value,
                      })
                    }
                    error={isFieldError("documentNumber")}
                  ></Form.Input>
                </Grid.Column>
              </Grid.Row>

              <Grid.Row>
                <Grid.Column>
                  <Form.Input
                    fluid
                    id="language"
                    label={"Language"}
                    placeholder="Language"
                    value={iadbMetadata.language}
                    onChange={(e) =>
                      setIADBMetadata({
                        ...iadbMetadata,
                        language: e.target.value,
                      })
                    }
                    error={isFieldError("language")}
                  ></Form.Input>
                </Grid.Column>
              </Grid.Row>
              <Grid.Row>
                <Grid.Column>
                  <Grid.Row className={styles.assetMappingRow}>
                    <Grid.Column>
                      <Form.Input
                        fluid
                        id="assetKey"
                        label={"Asset"}
                        placeholder="Add Asset"
                        value={mapping.asset}
                        onChange={(e) =>
                          setMapping({
                            ...mapping,
                            asset: e.target.value,
                          })
                        }
                        error={isAssetAdded && mapping.asset.length === 0}
                      ></Form.Input>
                    </Grid.Column>
                    <Grid.Column textAlign="left">
                      <Form.Input
                        fluid
                        id="assetValue"
                        label={"Section"}
                        placeholder="Add Section"
                        value={mapping.section}
                        onChange={(e) =>
                          setMapping({
                            ...mapping,
                            section: e.target.value,
                          })
                        }
                        error={isAssetAdded && mapping.section.length === 0}
                      ></Form.Input>
                    </Grid.Column>
                  </Grid.Row>
                  <Grid.Row className={styles.addMore}>
                    <span
                      onClick={handleAddClick}
                      className={styles.addMoreBtn}
                    >
                      {assetMappings.length === 0 ? "Add" : "Add More"}
                    </span>
                  </Grid.Row>
                </Grid.Column>
              </Grid.Row>
              {assetMappings.length !== 0 && (
                <Grid.Row>
                  <Grid.Column className={styles.labelGroup}>
                    {assetMappings.map((mappingItem, index) => {
                      return (
                        <Button
                          className={styles.assetMappingBtn}
                          size="mini"
                          as="div"
                          labelPosition="right"
                        >
                          <Button size="mini" className={styles.assetMapping}>
                            {mappingItem.asset}
                          </Button>
                          <Label
                            className={styles.sectionMapping}
                            basic
                            pointing="left"
                          >
                            {mappingItem.section}
                          </Label>
                          <Icon
                            onClick={() => handleRemoveMapping(index)}
                            className={styles.deleteMapping}
                            name="close"
                          />
                        </Button>
                      );
                    })}
                  </Grid.Column>
                </Grid.Row>
              )}

              <Grid.Row columns={2}>
                <Grid.Column>
                  {isFormSubmitted && (
                    <div className={cx(styles.error)}> {error}</div>
                  )}
                  {!isFormSubmitted && isAssetAdded && (
                    <div className={cx(styles.error)}> {error}</div>
                  )}
                </Grid.Column>
                <Grid.Column textAlign="right">
                  <SaveChanges
                    onClick={(e) => onCreateMetaData(e)}
                    content={renderContent()}
                    type="submit"
                  />
                  <Cancel
                    content="Cancel"
                    onClick={() => handleCancelClick()}
                  />
                </Grid.Column>
              </Grid.Row>
            </Grid>
          </Form>
        </Modal.Content>
      </Modal>
    </>
  );
}
